import {createRouter, createWebHistory} from "vue-router";
import {getCurrentUser} from "@/lib/utils";
import {DateTime} from "luxon";
import {useAuth} from '@/lib/useAuth'

/**
 * Routen die gesperrt sind (z.B. noch in Entwicklung).
 * Pfade hier eintragen → User wird auf /maintenance weitergeleitet.
 */
export const blockedRoutes: string[] = [
     '/reporting',
]

const routes = [
    {
        path: "/", redirect: "/home"
    },
    {
        path: "/tasks",
        component: () => import("@/views/tasks/TaskListAll.vue"),
        props: {
            icon: "mdi-format-list-checks"
        }
    },
    {
        path: "/tasks/mine",
        component: () => import("@/views/tasks/TaskListAll.vue"),
        props: () => ({
            pageTitle: "Meine Aufgaben",
            icon: "mdi-account-check",
            defaultFilters: {
                assignee: getCurrentUser()
            }
        })
    },
    {
        path: "/tasks/overdue",
        component: () => import("@/views/tasks/TaskListAll.vue"),
        props: {
            pageTitle: "Überfällige Aufgaben",
            icon: "mdi-timer-sand-complete",
            defaultFilters: {
                scheduled_end: `<${DateTime.now().setZone("Europe/Berlin").toFormat("yyyy-MM-dd'T'HH:mm")}`,
                state: "!=BEEND, !=UNERL_BEEND, !=GESCHL",
            }
        }
    },
    {
        path: "/lockouts",
        component: () => import("@/views/tasks/TaskListAll.vue"),
        props: {
            pageTitle: "Alle Freischaltungen",
            icon: "mdi-home-lightning-bolt-outline",
            defaultFilters: {
                is_lockout: true
            }
        }
    },
    {
        path: "/lockouts/mine",
        component: () => import("@/views/tasks/TaskListAll.vue"),
        props: () => ({
            pageTitle: "Meine Freischaltungen",
            icon: "mdi-lightning-bolt",
            defaultFilters: {
                is_lockout: true,
                assignee: getCurrentUser(),
                state: "!BEEND,GESCHL,UNERL_BEEND"
            }
        })
    },
    {
        path: "/orders",
        component: () => import("@/views/orders/OrderListAll.vue")
    },
    {
        path: "/orders/progress",
        component: () => import("@/views/orders/OrderListAll.vue"),
        props: {
            pageTitle: "Aufträge in Bearbeitung",
            icon: "mdi-progress-clock",
            defaultFilters: {
                state: "INBEARB"
            }
        }
    },
    {
        path: "/orders/finished",
        component: () => import("@/views/orders/OrderListAll.vue"),
        props: {
            pageTitle: "Abgeschlossene Aufträge",
            icon: "mdi-progress-clock",
            defaultFilters: {
                state: "BEEND, GESCHLOSS"
            }
        }
    },
    {
        path: "/orders/failed",
        component: () => import("@/views/orders/OrderListAll.vue"),
        props: {
            pageTitle: "Unerledigt beendete Aufträge",
            icon: "mdi-progress-clock",
            defaultFilters: {
                state: "UNERL_BEEND"
            }
        }
    },
    {
        path: "/checklists/templates",
        component: () => import("@/views/checklists/ChecklistTemplateOverview.vue")
    },
    {
        path: "/checklists/all",
        component: () => import("@/views/checklists/ChecklistAll.vue")
    },
    {
        path: "/checklists/mine",
        component: () => import("@/views/checklists/ChecklistAll.vue"),
        props: () => ({
            pageTitle: "Meine Checklisten",
            icon: "mdi-list-status",
            defaultFilters: {
                state: "!UNERL_BEEND, BEEND",
                assignees: getCurrentUser(),
            }
        })
    },
    {
        path: "/test",
        component: () => import("@/views/StaffScheduling.vue")
    },
    {
        path: "/account",
        component: () => import("@/views/Account.vue")
    },
    {
        path: "/motorenliste",
        component: () => import("@/views/assets/DriveList.vue")
    },
    {
        path: "/reporting",
        component: () => import("@/views/reporting/ReportingOverview.vue")
    },
    {
        path: "/reporting/orders",
        component: () => import("@/views/reporting/Reporting.vue")
    },
    {
        path: "/outages",
        component: () => import("@/views/outages/Outage.vue")
    },
    {
        path: "/outages/:code",
        component: () => import("@/views/outages/OutageDetail.vue"),
        props: true
    },
    {
        path: "/settings",
        component: () => import("@/views/Settings.vue")
    },
    {
        path: "/home",
        component: () => import("@/views/Dashboard.vue")
    },
    {
        path: "/login",
        component: () => import("@/components/forms/LoginForm.vue"),
        meta: {public: true}
    },
    {
        path: "/991register991",
        component: () => import("@/components/forms/RegisterForm.vue"),
        meta: {public: true}
    },
    {
        path: "/maintenance",
        component: () => import("@/views/Maintenance.vue"),
    },
    {
        path: "/staffScheduling",
        component: () => import("@/views/StaffScheduling.vue"),
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})


// Öffentliche Routen: /login, /register
// Alles andere erfordert Anmeldung
router.beforeEach(async (to, from, next) => {
    const {user, loading} = useAuth()

    // Warten bis Auth-State geladen ist
    if (loading.value) {
        await new Promise<void>(resolve => {
            const checkLoading = setInterval(() => {
                if (!loading.value) {
                    clearInterval(checkLoading)
                    resolve()
                }
            }, 50)
        })
    }

    // Öffentliche Seiten immer erlauben
    if (to.meta.public) {
        // Wenn bereits angemeldet, zu /home weiterleiten
        if (user.value) {
            next('/home')
        } else {
            next()
        }
        return
    }

    // Gesperrte Routen → Wartungsseite
    if (blockedRoutes.some(r => to.path.startsWith(r)) && to.path !== '/maintenance') {
        next('/maintenance')
        return
    }

    // Geschützte Seiten: Login erforderlich
    if (!user.value) {
        next('/login')
    } else {
        next()
    }
})

export default router;
